import { BrowserRouter } from "react-router-dom";
import { AppRoutes } from "./router";
import { I18nextProvider } from "react-i18next";
import i18n from "./i18n";
import { ReactNode, useEffect } from "react";
import { ThemeContext, useThemeInit } from "./hooks/useTheme";

function ThemeProvider({ children }: { children: ReactNode }) {
  const themeState = useThemeInit();
  return (
    <ThemeContext.Provider value={themeState}>
      {children}
    </ThemeContext.Provider>
  );
}

/*
  Smooth scrolling for in-page anchor links (e.g. <a href="#pricing">).
  Replaces the global `html { scroll-behavior: smooth }` — which made
  regular wheel scrolling feel sticky — with an opt-in JS handler that
  only animates when the user actually clicks an anchor to a real target.
  Nav buttons already scroll via their own JS handlers and are unaffected.
*/
function useSmoothAnchorScroll() {
  useEffect(() => {
    const onClick = (e: MouseEvent) => {
      if (e.defaultPrevented || e.button !== 0 || e.metaKey || e.ctrlKey || e.shiftKey || e.altKey) {
        return;
      }
      const anchor = (e.target as HTMLElement)?.closest?.('a[href^="#"]') as HTMLAnchorElement | null;
      if (!anchor) return;
      const hash = anchor.getAttribute('href');
      if (!hash || hash === '#') return;
      let target: Element | null = null;
      try { target = document.querySelector(hash); } catch { return; }
      if (!target) return;
      e.preventDefault();
      const reduceMotion = window.matchMedia?.('(prefers-reduced-motion: reduce)').matches;
      target.scrollIntoView({ behavior: reduceMotion ? 'auto' : 'smooth', block: 'start' });
    };
    document.addEventListener('click', onClick);
    return () => document.removeEventListener('click', onClick);
  }, []);
}

function App() {
  useSmoothAnchorScroll();
  return (
    <I18nextProvider i18n={i18n}>
      <ThemeProvider>
        <BrowserRouter basename={__BASE_PATH__}>
          <AppRoutes />
        </BrowserRouter>
      </ThemeProvider>
    </I18nextProvider>
  );
}

export default App;